import './Modal.scss';
import './Modal.m.scss';
import { QStrapContainer } from '@components/Component/Container/QStrapContainer/QStrapContainer.js';
import { ButtonsMixin } from '@components/Mixin/ButtonsMixin/ButtonsMixin.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { Component } from '@components/Component/Component.js';
import { Subject } from 'rxjs';
import { DataRecord } from '@components/DataRecord/DataRecord.js';
import { createElement, debounce, getID, isMobile } from '@components/Utility/index.js';
/**
 * Base `Modal` class.
 *
 * @example
* 		component_testmodal(modal) {
 * 			modal.before('open', () => {
 * 				this.records.modalrecord.set('somecolumn', 'default val');
 * 				this.records.modalrecord.set('somecolumn2', 'default val 2');
 *
 * 				return true;
 * 			})
 *
 * 			modal.before('cancel', () => {
 * 				// some logic to decide if cancelling is allowed
 * 				alert('cancelling blocked');
 * 				return false;
 * 			})
 *
 * 			modal.before('submit', (value) => {
 * 				if (value != 2) {
 * 	 			alert('wrong value, submission blocked');
 * 					return false;
 * 				}
 *
 * 				return true;
 * 			})
 * 		}
 *
 * 		component_modalopenbutton(button) {
 * 			button.on('click', () => {
 *
 * 				this.componentGet('testmodal').open()
 * 					.subscribe((value) => {
 * 						console.log(value + ' was submitted')
 * 					}
 * 					,() => {
 * 						console.error('modal cancelled');
 * 					})
 *
 * 			})
 * 		}
 *
 * 		component_modalsubmitbutton(button) {
 * 			button.on('click', () => {
 * 				let submit = prompt('1+1');
 * 				this.componentGet('testmodal').submit(submit);
 * 			})
 * 		}
 */
export class Modal extends ButtonsMixin(QStrapContainer) {
    get title() { return this._title || ''; }
    set title(value) {
        this._propertySet('title', value);
        this.renderTitle();
    }
    get submitCaptionKey() {
        return this._submitCaptionKey;
    }
    set submitCaptionKey(value) {
        this._propertySet('submitCaptionKey', value);
        const button = this.buttonGet('submit');
        if (button) {
            button.captionKey = value;
            button.caption = this.CaptionManager.getCaption(`${button.captionKey}_caption`);
            if (value && !this.CaptionManager.hasKey(value)) {
                this.CaptionManager.onLoad$(value, true).subscribe(() => {
                    if (this.drawn) {
                        this.renderButtons(button.containerId);
                    }
                });
            }
            this.refreshButton('submit');
        }
    }
    get modalStyle() { return this._modalStyle; }
    set modalStyle(style) {
        if (this.drawn && this._modalStyle !== style) {
            this.rootElement?.classList.remove(`modal-${this._modalStyle}`);
        }
        this._propertySet('modalStyle', style);
        this.renderModalStyle();
    }
    renderModalStyle() {
        if (['error', 'destructive'].indexOf(this._modalStyle || '') > -1) {
            this.buttonClassListUpdate('submit', 'destructive', true);
        }
        else {
            this.buttonClassListUpdate('submit', 'destructive', false);
        }
        this.refreshButton('submit');
        if (this.drawn && this._modalStyle) {
            this.rootElement.classList.add(`modal-${this._modalStyle}`);
            const iconElement = this.rootElement.querySelector('.modal-style-icon i');
            this.IconManager.updateIcon(iconElement, this.modalStyleIconGet());
        }
    }
    modalStyleIconGet() {
        switch (this.modalStyle) {
            case 'error': return 'error';
            case 'destructive': return 'delete';
            case 'warning': return 'warning';
            case 'success': return 'check_circle';
            case 'info': return 'info';
            default: return '';
        }
    }
    get includeSubmitButton() { return this._includeSubmitButton || false; }
    set includeSubmitButton(value) {
        this._propertySet('includeSubmitButton', value);
        this.refreshButton('submit');
    }
    get fullScreen() { return this._fullScreen; }
    set fullScreen(value) {
        this._propertySet('fullScreen', value);
        this.renderFullScreen();
    }
    renderFullScreen() {
        if (this.drawn) {
            this.rootElement.classList.toggle('modal-fullscreen', this._fullScreen);
        }
    }
    get sheet() { return this._sheet; }
    set sheet(value) {
        this._propertySet('sheet', value);
        this.renderSheet();
    }
    get isSheetActive() {
        return this._sheet && !this._fullScreen && isMobile();
    }
    renderSheet() {
        if (this.drawn) {
            const active = this.isSheetActive;
            this.rootElement.classList.toggle('modal-sheet', active);
            if (active) {
                this.rootElement.style.top = '';
                this.rootElement.style.left = '';
            }
            else {
                this.centerModal();
            }
        }
    }
    constructor(id, rootElement, dependencies = {}) {
        super(id, rootElement, dependencies);
        this.componentName = 'Modal';
        this._submitCaptionKey = 'system_ok';
        this._modalStyle = '';
        this._fullScreen = false;
        this._sheet = true;
        this.hotkeys = {
            ...this.hotkeys // we are extending the hotkeys from the parent class. 
            ,
            'enter': () => {
                return this.handleEnterKey();
            },
            'escape': () => {
                this.buttonClick('_close');
                return true;
            }
        };
        this.rootElement = rootElement || createElement('div');
        if (this.formContext) {
            this.rootElement.setAttribute('data-componentid', id);
        }
        this.initializeButtons();
    }
    initializeProperties() {
        super.initializeProperties();
        this.hidden = true; // initially hidden
        this._includeSubmitButton = false;
        this.attachedToDOM = false;
        this.destroyOnSubmit = true;
        this.autoCenter = true;
        this.lazyDrawContent = true;
    }
    captionTypesSet() {
        this.captionTypes = {
            title: 'title'
        };
    }
    initializeButtons() {
        this.buttonAdd({
            icon: 'close',
            containerId: 'control',
            tooltip: this.CaptionManager.getCaption('system_modal_close_tooltip'),
            id: '_close' // needed to add the _ prefix so that process buttons can use the id close instead. 
            ,
            onClick: () => {
                this.cancel();
            }
        });
        this.buttonAdd({
            containerId: '',
            captionKey: this.submitCaptionKey,
            id: 'submit'
            // ,eventConfigName: 'submit'
            ,
            includeProperty: 'includeSubmitButton',
            onClick: () => {
                this.handleSubmit();
            }
        });
    }
    primaryFieldComponentGet() {
        const fieldElement = this.childContainerGet()?.querySelector('.s360Field');
        if (fieldElement) {
            const componentId = fieldElement.getAttribute('data-global-id');
            if (componentId) {
                return DIContainer.getInstance('ComponentRegistry').getComponent(componentId);
            }
        }
    }
    primaryGridComponentGet() {
        const gridElement = this.childContainerGet()?.querySelector('.s360Grid');
        if (gridElement) {
            const gridId = gridElement.getAttribute('data-global-id');
            if (gridId) {
                return DIContainer.getInstance('ComponentRegistry').getComponent(gridId);
            }
        }
    }
    handleSubmit() {
        const error = this.validateFields();
        if (error) {
            this.alert({ caption: error, modalStyle: 'warning' });
            return;
        }
        if (this.primaryGridComponentGet()) {
            this.submit(this.primaryGridComponentGet().getSelectedRecords());
        }
        else if (this.record) {
            this.submit(this.record);
        }
        else {
            this.submit(undefined);
        }
    }
    handleEnterKey() {
        console.log('ENTER KEY PRESSED FOR MODAL');
        if (this.primaryGridComponentGet()) {
            this.primaryGridComponentGet().refreshData();
        }
        else {
            this.debouncedSubmit();
        }
        return true; // tell the hotkey event to stop propagating upward. 
    }
    debouncedSubmit() {
        this.handleSubmit();
    }
    draw() {
        this.rootElement.classList.add('s360Modal'); // anything that extends modal will need the styles
        super.draw();
        this.renderModalStyle();
        this.renderFullScreen();
        this.renderSheet();
        this.contentContainer = this.childContainerGet();
        this.loadingThrobberRoot = this.contentContainer;
        this.setupDragEvents();
        if (!this.hidden) {
            this.renderHiddenState();
        }
        this.formDesignerSetup();
        this.debouncedSubmit = debounce(this.debouncedSubmit, 50);
    }
    formDesignerSetup() {
        if (this.formDesignerMode) {
            const addComponentButton = DIContainer.getInstance('IconManager').getIconElement('add');
            addComponentButton.classList.add('form-designer-add');
            const modalContent = this.rootElement.querySelector('.modal-content');
            modalContent.appendChild(addComponentButton);
        }
    }
    formDesignerPropertiesGet(properties) {
        properties = super.formDesignerPropertiesGet(properties);
        properties.width = ({
            id: 'width',
            value: this._width,
            componentType: 'Input',
            componentProperties: {},
            captionKey: 'formbuilder_width',
            propertyGroup: 'layout',
            saveFlag: () => !!this._width
        });
        return properties;
    }
    drawChildren() {
        super.drawChildren();
        const grid = this.primaryGridComponentGet();
        if (grid && !grid.propertyGet('eventHandlers')['selectbutton']) {
            grid.on('selectbutton', (_superHandler, selectedRecords) => {
                if (selectedRecords.length) {
                    this.submit(selectedRecords);
                }
            });
        }
    }
    // TODO: this is pretty similar to ScreenWindow. Not sure if maybe we can somehow share code between them.. but screenWindow cant really extend Modal since it needs to extend Screen.
    // not sure if a mixin would make sense especially for just two components. ResizeMixin maybe. And MoveMixin. 
    // or a utility function in DragManager that injects all of the resize handles that are requested. Like $.resizable kinda. 
    /**
     * Sets up the event handlers for moving and resizing the `Modal`.
     */
    setupDragEvents() {
        const modalElement = this.rootElement;
        const dragManager = DIContainer.getInstance('DragManager');
        const windowBar = modalElement.querySelector('.modal-drag-area');
        const resizerRight = modalElement.querySelector('.resizer-right');
        const resizerLeft = modalElement.querySelector('.resizer-left');
        const resizerBottom = modalElement.querySelector('.resizer-bottom');
        const resizerBottomRight = modalElement.querySelector('.resizer-bottom-right');
        const resizerBottomLeft = modalElement.querySelector('.resizer-bottom-left');
        // TODO: we could make dragManager inject the resize handles into the component instead of manually creating them in each components template
        // consolidate this same code thats in screenwindow.ts
        const mouseDownDelegate = () => { this.autoCenter = false; };
        const dropDelegate = this.handleResizeDrop.bind(this);
        const dragContainer = this.rootElement;
        const dragDelegate = this.handleDragResize.bind(this);
        if (windowBar) {
            dragManager.setupMouseDownEvent(this, windowBar, { dragType: 'move', dragContainer, mouseDownDelegate });
        }
        if (resizerRight) {
            dragManager.setupMouseDownEvent(this, resizerRight, { dragType: 'resize-r', dragContainer, mouseDownDelegate, dropDelegate, dragDelegate });
        }
        if (resizerLeft) {
            dragManager.setupMouseDownEvent(this, resizerLeft, { dragType: 'resize-l', dragContainer, mouseDownDelegate, dropDelegate, dragDelegate });
        }
        if (resizerBottom) {
            dragManager.setupMouseDownEvent(this, resizerBottom, { dragType: 'resize-b', dragContainer, mouseDownDelegate, dragDelegate });
        }
        if (resizerBottomRight) {
            dragManager.setupMouseDownEvent(this, resizerBottomRight, { dragType: 'resize-br', dragContainer, mouseDownDelegate, dropDelegate, dragDelegate });
        }
        if (resizerBottomLeft) {
            dragManager.setupMouseDownEvent(this, resizerBottomLeft, { dragType: 'resize-bl', dragContainer, mouseDownDelegate, dropDelegate, dragDelegate });
        }
    }
    handleDragResize(event, dragInfo) {
        const dragManager = DIContainer.getInstance('DragManager');
        dragManager.resizeContainer(event, dragInfo);
    }
    handleResizeDrop(_event, _dragInfo) {
        this.updateScreenBreakpoint();
    }
    renderHiddenState() {
        if (this.drawn && !this._hiddenState) {
            // lazy draw children first time showing the modal.
            this.drawChildren();
        }
        super.renderHiddenState();
        if (this.drawn && !this._hiddenState) {
            this.updateScreenBreakpoint();
            this.enableResizeObserver();
        }
        else {
            this.disableResizeObserver();
            this.autoCenter = true;
        }
    }
    updateScreenBreakpoint() {
        super.updateScreenBreakpoint();
        const buttonContainer = this.buttonContainerGet('');
        if (buttonContainer) {
            const width = buttonContainer.offsetWidth;
            if ((isMobile() && width < 500) || width < 100) {
                buttonContainer.classList.add('button-container-sm');
                buttonContainer.classList.remove('button-container-md');
                buttonContainer.classList.remove('button-container-lg');
            }
            else if (width > 350) {
                buttonContainer.classList.add('button-container-lg');
                buttonContainer.classList.remove('button-container-md');
                buttonContainer.classList.remove('button-container-sm');
            }
            else {
                buttonContainer.classList.add('button-container-md');
                buttonContainer.classList.remove('button-container-lg');
                buttonContainer.classList.remove('button-container-sm');
            }
        }
    }
    enableResizeObserver() {
        const resizeMgr = DIContainer.getInstance('ResizeManager');
        resizeMgr.observe(this.rootElement, this.centerModal.bind(this));
    }
    /**
     * Opens a modal and triggers the "open" event which is blockable with modal.before('open', ...)
     * Use Open when initially opening a modal, as opposed to show. Use show/hide if you want to temporarily hide a modal but maintain its state for later.
     * Returns an observable so that when the modal is submitted the code that opened it is informed.
     */
    open(options = {}) {
        const modalResult = new Subject();
        if (options.modalStyle && options.modalStyle !== this._modalStyle) {
            this.modalStyle = options.modalStyle;
        }
        if (options.attachToElement) {
            this.attachModal(options.attachToElement);
        }
        if (!this.attachedToDOM) {
            this.attachModal(document.body);
        }
        if (options.sheet !== undefined) {
            this.sheet = options.sheet;
        }
        if (options.submitCaptionKey) {
            this.submitCaptionKey = options.submitCaptionKey;
        }
        this.trigger('open', {
            defaultEventHandler: () => {
                // automatically draw modal on display if it hasnt been yet. 
                if (!this.drawn /* && !this.containerId*/) {
                    this.draw();
                }
                this.show();
            },
            eventBlockedHandler: () => {
                // The open event was blocked, emit an error
                modalResult.error(new Error('Silent: Modal open event was blocked.'));
            },
            postEventHandler: () => {
                // Modal is now open
                this.modalResult$ = modalResult;
            }
        });
        return modalResult.asObservable();
    }
    initialFocusComponentGet() {
        const field = this.primaryFieldComponentGet();
        if (field) {
            return field;
        }
        const grid = this.primaryGridComponentGet();
        if (grid) {
            return grid;
        }
        return this;
    }
    primaryButtonFocus() {
        let buttonElement = this.childContainer?.querySelector('button:not(.disabled):not(.hidden)');
        if (!buttonElement) {
            buttonElement = this.buttonContainerGet('')?.querySelector('button:not(.disabled):not(.hidden)');
        }
        buttonElement?.focus();
    }
    show() {
        super.show();
        const focusComponent = this.initialFocusComponentGet();
        if (focusComponent) {
            focusComponent.focus();
            if (focusComponent === this) {
                // means there are no inputs or grid, we can focus the primary button instead. 
                this.primaryButtonFocus();
            }
        }
        if (this.screenCoverRoot !== 'none' && !this.screenCover) {
            let root;
            if (this.screenCoverRoot !== 'app') {
                root = this.rootElement.closest('.s360Form');
                if (!root) {
                    root = DIContainer.getInstance('Application').rootElement;
                }
            }
            else {
                root = DIContainer.getInstance('Application').rootElement;
            }
            if (root) {
                this.screenCover = createElement('div', ['screen-cover']);
                this.screenCover.style.zIndex = Modal.zIndex.toString();
                Modal.zIndex++;
                this.rootElement.style.zIndex = Modal.zIndex.toString();
                root.appendChild(this.screenCover);
            }
        }
    }
    hide() {
        super.hide();
        if (this.screenCover) {
            this.screenCover.remove();
            Modal.zIndex--;
            this.screenCover = null;
        }
    }
    /**
     * Close the modal when done with it. Use close instead of hide. Use show/hide if you want to temporarily hide a modal but maintain its state for later.
     * Triggers the modal close event which is blockable with modal.before('close', ...)
     */
    close(cancelled = false) {
        this.trigger('close', {
            defaultEventHandler: () => {
                this.hide();
                if (this.modalResult$) {
                    this.modalResult$.complete();
                }
                if (this.destroyOnSubmit) {
                    this.destroy();
                }
            }
        }, cancelled);
    }
    /**
     * Programmatically submit the modal (ie. from some custom button click handler defined in form level code)
     * This will trigger the submit event which is blockable with modal.before('submit', ...)
     * Triggers the observable that is returned by the "open" function.
     * @param value
     */
    submit(value) {
        this.trigger('submit', {
            defaultEventHandler: (value) => {
                if (this.modalResult$) {
                    this.modalResult$.next(value);
                    // this.modalResult$.complete(); // completes in close.. lets say the close is blocked, we might want to allow a second submission. 
                }
                this.close();
            }
        }, value);
    }
    /**
     * Called when user clicks the modal exit button.
     * This will trigger the cancel event which is blockable with modal.before('cancel', ...)
     * Triggers an error on the observable that was returned by the "open" function.
     */
    cancel() {
        this.trigger('cancel', {
            defaultEventHandler: () => {
                if (this.modalResult$) {
                    const err = new Error('Silent: Modal action was cancelled.');
                    err.isCancel = true;
                    this.modalResult$.error(err);
                }
                this.close(true);
            }
        });
    }
    disableResizeObserver() {
        const resizeMgr = DIContainer.getInstance('ResizeManager');
        resizeMgr.unobserve(this.rootElement);
    }
    // TODO: switch to getter/setter functions as discussed with Joe and Justin
    /**
     * Updates the modal title in the DOM
     */
    renderTitle() {
        if (this.drawn) {
            this.rootElement.querySelector('.modal-title').innerText = this.title;
        }
    }
    centerModal() {
        if (this.isSheetActive) {
            return;
        }
        if (this.autoCenter) {
            const container = this.getContainerDimensions(); // Get container dimensions
            const modalSize = this.getModalDimensions(); // Get modal dimensions
            // Calculate the top and left values
            const top = Math.max(0, (container.height - modalSize.height) / 2);
            const left = (container.width - modalSize.width) / 2;
            // Apply the top and left values to the modal's style
            this.rootElement.style.top = `${top}px`;
            this.rootElement.style.left = `${left}px`;
        }
    }
    // TODO: do we even need this?
    // resetModalSize() {
    // 	this.renderWidth();
    // 	this.renderHeight();
    //	
    // 	// this.rootElement.style.width = '';
    // 	// this.rootElement.style.height = '';
    // }
    getContainerDimensions() {
        // Assuming the container is the parent element of the modal
        const container = this.rootElement.parentElement;
        return {
            width: container?.clientWidth || 0,
            height: container?.clientHeight || 0
        };
    }
    getModalDimensions() {
        return {
            width: this.rootElement.offsetWidth,
            height: this.rootElement.offsetHeight
        };
    }
    template() {
        const icon = DIContainer.getInstance('IconManager').getIconString('');
        this.rootElement.tabIndex = 0; // Make it focusable
        const template = `
			<div class="modal-bar">
				<div class="modal-drag-area">
					<span class="modal-style-icon">${icon}</span>
					<span class="modal-title">${this.title}</span>
				</div>
				<div class="control-button-container">
				
				</div>
			</div>
			${this.modalContentTemplate()}
			<div class="resizer resizer-left"></div>
			<div class="resizer resizer-right"></div>
			<div class="resizer resizer-bottom"></div>
			<div class="resizer resizer-bottom-right"></div>
			<div class="resizer resizer-bottom-left"></div>
		`;
        return template;
    }
    modalContentTemplate() {
        return `<div class="modal-content">
				<div class="child-container"></div>
				<div class="button-container"></div>
			</div>`;
    }
    buttonContainerCreate() {
        // for buttons like the exit button
        const buttonContainerControls = this.rootElement.querySelector('.control-button-container');
        this.buttonContainers.set('control', buttonContainerControls);
        // for submit buttons
        const buttonContainer = this.rootElement.querySelector('.button-container');
        this.buttonContainers.set('', buttonContainer);
    }
    appendLoadingThrobber() {
        if (this.contentContainer) {
            super.appendLoadingThrobber();
        }
    }
    destroy() {
        this.disableResizeObserver();
        if (this.modalResult$) {
            this.modalResult$.complete();
            this.modalResult$ = undefined;
        }
        if (this.screenCover) {
            this.screenCover.remove();
            Modal.zIndex--;
            this.screenCover = null;
        }
        this.attachedToElement = undefined;
        super.destroy();
    }
    /**
     * Attaches a modal to the nearest form to the specified element or appends it to the document body if no form is found.
     *
     * @param {HTMLElement} element - The element to attach the modal to.
     * @returns {void}
     */
    attachModal(element) {
        if (!this.rootElement) {
            this.rootElement = document.createElement('div');
        }
        if (element instanceof DataRecord) {
            if (element.formGlobalId) {
                element = DIContainer.getInstance('ComponentRegistry').getComponent(element.formGlobalId);
            }
            else {
                element = undefined;
            }
        }
        if (element instanceof Component) {
            element = element.rootElement;
        }
        // TODO: if modal has inline sizing, make sure it fits inside whatever container.
        this.attachedToDOM = true;
        let currentElement = element;
        // Traverse up the DOM tree to find a form to inject our modal into.
        while (currentElement && currentElement !== document.body) {
            if (currentElement.classList.contains('s360Form')) {
                // If found, append the modal to this element
                this.attachedToElement = currentElement;
                if (document.fullscreenElement) {
                    document.fullscreenElement.appendChild(this.rootElement);
                    return;
                }
                currentElement.appendChild(this.rootElement);
                return;
            }
            // Move up to the parent element
            currentElement = currentElement.parentElement;
        }
        // check for the ScreenCover and attach there if it's active
        const sm = DIContainer.getInstance('ScreenManager');
        if (sm.screenCoverActive) {
            sm.activeScreenGet()?.rootElement.appendChild(this.rootElement);
            return;
        }
        this.attachedToElement = document.body;
        if (document.fullscreenElement) {
            document.fullscreenElement.appendChild(this.rootElement);
            return;
        }
        // If the loop completes without finding a .s360Form element, append to document.body
        document.body.appendChild(this.rootElement);
    }
    handleFullscreenSwitch(fullscreenElement) {
        super.handleFullscreenSwitch(fullscreenElement);
        if (this.attachedToElement) {
            if (fullscreenElement) {
                fullscreenElement.appendChild(this.rootElement);
            }
            else {
                this.attachedToElement.appendChild(this.rootElement);
            }
        }
    }
    defaultEmptyRecordValuesGet() {
        return {};
    }
    createEmptyRecord() {
        this.linkRecord(new DataRecord(getID('modalrecord'), { formGlobalId: this.formContext?.globalId }));
        this.record.load(this.defaultEmptyRecordValuesGet());
        this.record.state = 'edit';
    }
    buttonDefaultsSet(button) {
        if (button.containerId !== 'control') {
            if (!button.classList) {
                button.classList = ['button-style'];
            }
            else if (!button.classList.includes('container-style')) {
                button.classList.push('button-style');
            }
        }
        return super.buttonDefaultsSet(button);
    }
}
Modal.zIndex = 998; // when a modal opens a second modal, the second modals screen cover needs to block interaction with the first modal.
